import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import CountUp from 'react-countup';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import config from '../../config';
import { getVentesRapport } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeStatusBadge, NdoeError, NdoeLoading,
} from '../../components/ndoe';
// Pont jetons CSS → canvas : les graphiques lisent la charte au lieu de figer
// des couleurs. Le jour où la charte bascule, ils suivent sans être retouchés.
import { jetons, alpha, palette, optionsBase, fusionner } from '../../components/ndoe/chartTheme';
import { toCsv, downloadCsv } from './exportCsv';
import { money, dateTxt } from './format';
import './rapports.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip, Legend);

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Période 7/30/90/180 → bornes calendaires (le backend hérité veut startDate/endDate).
const bornes = (period) => {
  if (period.period === 'custom') return { startDate: period.start_date, endDate: period.end_date };
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (Number(period.period) - 1));
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: ymd(start), endDate: ymd(end) };
};

export default function RapportDepenses() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [period, setPeriod] = useState({ period: 30 });
  const [depenses, setDepenses] = useState(null);
  const [ventes, setVentes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const dates = useMemo(() => bornes(period), [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, v] = await Promise.all([
        axios.get(`${DOMAIN}/api/rapport/depense-rapport-global`, { params: { startDate: dates.startDate, endDate: dates.endDate } }),
        getVentesRapport({ start_date: dates.startDate, end_date: dates.endDate }),
      ]);
      setDepenses(d.data);
      setVentes(v.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport des dépenses.');
    } finally { setLoading(false); }
  }, [dates]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  // --- Chiffres composés -------------------------------------------------------
  const totalPeriode = useMemo(
    () => (depenses?.totalPeriodData || []).reduce((s, x) => s + num(x.total_periode), 0),
    [depenses],
  );
  const kpisVentes = ventes?.kpis || {};
  const ca = num(kpisVentes.chiffre_affaires);
  const marge = kpisVentes.marge_estimee;
  const couverture = num(kpisVentes.marge_couverture_pct);
  // Coût des marchandises vendues : reconstitué depuis la marge (CA couvert − marge).
  const coutMarchandises = marge !== null && marge !== undefined && couverture > 0
    ? (ca * (couverture / 100)) - num(marge)
    : null;
  const resultat = coutMarchandises !== null && couverture >= 80
    ? ca - coutMarchandises - totalPeriode
    : null;
  const resultatAvantCout = ca - totalPeriode;

  const handleExport = () => {
    if (!depenses) return;
    setExporting(true);
    try {
      const blocs = [
        ...(depenses.monthlyData || []).map((m) => ({ type: 'Mois', nom: m.mois, montant: num(m.total_mensuel) })),
        ...(depenses.typeData || []).map((t) => ({ type: 'Type (historique)', nom: t.type, montant: num(t.total_type) })),
        ...(depenses.beneficiaryData || []).map((b) => ({ type: 'Bénéficiaire (historique)', nom: b.beneficiaire || '—', montant: num(b.total_beneficiaire) })),
      ];
      const columns = [
        { title: 'Type', value: (r) => r.type },
        { title: 'Libellé', value: (r) => r.nom },
        { title: 'Montant ($)', value: (r) => r.montant },
      ];
      downloadCsv(`depenses_${dates.startDate}_${dates.endDate}.csv`, toCsv(columns, blocs));
      toast.success('Rapport exporté.');
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  // Jetons lus DANS le rendu, jamais au niveau module : au chargement du module
  // le CSS n'est pas garanti appliqué, getComputedStyle rendrait du vide et le
  // pont retomberait EN SILENCE sur ses valeurs de repli.
  const t = jetons();
  // Palette CATÉGORIELLE pour les types de dépense : ce sont des étiquettes sans
  // ordre ni valeur morale (Loyer n'est pas « pire » que Transport), donc aucune
  // couleur sémantique ici.
  const couleursTypes = palette();

  const mensuel = (depenses?.monthlyData || []).slice(-12);
  const types = (depenses?.typeData || []).slice().sort((a, b) => num(b.total_type) - num(a.total_type));
  const benef = (depenses?.beneficiaryData || []).slice().sort((a, b) => num(b.total_beneficiaire) - num(a.total_beneficiaire)).slice(0, 8);

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Dépenses et résultat"
        subtitle="Ce que la boutique dépense — et ce qu'il reste une fois la marchandise et les frais payés."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        <span className="pp-range">du {dateTxt(dates.startDate)} au {dateTxt(dates.endDate)}</span>
      </div>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : loading || !depenses ? (
        <NdoeLoading rows={6} />
      ) : (
        <>
          <div className="rv-kpis">
            <div className="rv-kpi">
              <small>Dépenses (période)</small>
              <b className="num"><CountUp end={totalPeriode} duration={0.9} separator=" " decimals={totalPeriode % 1 ? 2 : 0} /> $</b>
            </div>
            <div className="rv-kpi">
              <small>Chiffre d'affaires (période)</small>
              <b className="num">{money(ca)}</b>
            </div>
            <div className="rv-kpi rv-kpi--gold">
              <small>{resultat !== null ? 'Résultat estimé' : 'CA − dépenses (avant marchandises)'}</small>
              <b className={`num ${((resultat ?? resultatAvantCout) < 0) ? 'is-neg' : ''}`}>
                {money(resultat !== null ? resultat : resultatAvantCout)}
              </b>
            </div>
          </div>

          {/* --- Mini compte de résultat -------------------------------------- */}
          <section className="ndoe-card rd-pnl">
            <div className="ndoe-section__title">Compte de résultat simplifié — période</div>
            <div className="rd-pnl__ligne"><span>Chiffre d'affaires (ventes nettes)</span><b className="num">{money(ca)}</b></div>
            <div className="rd-pnl__ligne">
              <span>− Coût des marchandises vendues{coutMarchandises !== null ? ` (connu sur ${couverture} % du CA)` : ''}</span>
              <b className="num">{coutMarchandises !== null ? money(-coutMarchandises) : '—'}</b>
            </div>
            <div className="rd-pnl__ligne"><span>− Dépenses de la période</span><b className="num">{money(-totalPeriode)}</b></div>
            <div className="rd-pnl__ligne rd-pnl__ligne--total">
              <span>= Résultat estimé</span>
              <b className={`num ${resultat !== null && resultat < 0 ? 'is-neg' : ''}`}>{resultat !== null ? money(resultat) : '—'}</b>
            </div>
            {resultat === null && (
              <p className="pp-note" style={{ marginTop: 8 }}>
                <NdoeStatusBadge kind="custom" tone="warn" label="Honnêteté" />
                {' '}Le résultat n'est calculé que lorsque le coût d'achat couvre au moins 80 % du CA
                (actuellement {couverture} %). Lancez le rattrapage dans «&nbsp;Réceptions → Coûts d'achat&nbsp;»
                pour donner vie à cette ligne — en attendant, le CA − dépenses est affiché sans le coût marchandises.
              </p>
            )}
          </section>

          <section className="ndoe-card rv-chart">
            <div className="ndoe-section__title">Dépenses mensuelles — 12 derniers mois</div>
            <div className="rv-chart__zone">
              <Bar
                data={{
                  labels: mensuel.map((m) => m.mois),
                  // Rouge de la charte : une dépense est une SORTIE d'argent, elle
                  // garde ici le code couleur du négatif employé partout ailleurs
                  // (résultat déficitaire, dette). Translucide pour que la grille
                  // reste lisible derrière les barres hautes.
                  datasets: [{ label: 'Dépenses', data: mensuel.map((m) => num(m.total_mensuel)), backgroundColor: alpha(t.danger, 0.65), borderRadius: 6, maxBarThickness: 40 }],
                }}
                options={fusionner(optionsBase(), {
                  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (i) => ` ${Number(i.parsed.y).toLocaleString('fr-FR')} $` } } },
                  scales: { x: { ticks: { font: { size: 10 } } }, y: { ticks: { callback: (v) => `${v} $` } } },
                })}
              />
            </div>
          </section>

          <div className="rd-deux">
            <section className="ndoe-card rd-bloc">
              <div className="ndoe-section__title">Par type de dépense</div>
              <div className="rd-donut">
                <Doughnut
                  data={{
                    labels: types.map((d) => d.type),
                    datasets: [{
                      data: types.map((d) => num(d.total_type)),
                      backgroundColor: types.map((_, i) => couleursTypes[i % couleursTypes.length]),
                      borderColor: t.paper,   /* sépare les parts sans trait noir */
                      borderWidth: 2,
                    }],
                  }}
                  options={fusionner(optionsBase({ axeY: false }), { cutout: '62%' })}
                />
              </div>
              <small className="rd-note">Répartition sur tout l'historique enregistré.</small>
            </section>
            <section className="ndoe-card rd-bloc">
              <div className="ndoe-section__title">Par bénéficiaire</div>
              <div className="rv-barres">
                {benef.map((b) => {
                  const max = num(benef[0]?.total_beneficiaire) || 1;
                  return (
                    <div className="rv-barre" key={b.beneficiaire || '—'} style={{ cursor: 'default' }}>
                      <span className="rv-barre__nom">{b.beneficiaire || 'Sans bénéficiaire'}</span>
                      <span className="rv-barre__track" aria-hidden="true">
                        <i style={{ width: `${Math.max(3, (num(b.total_beneficiaire) / max) * 100)}%` }} />
                      </span>
                      <span className="rv-barre__val num">{money(num(b.total_beneficiaire))}</span>
                    </div>
                  );
                })}
              </div>
              <small className="rd-note">Sur tout l'historique enregistré.</small>
            </section>
          </div>
        </>
      )}
    </NdoePage>
  );
}
